import {
  AppEventTypes,
  FRIENDLY_CONNECTIVITY_MESSAGE,
  classifyThrownFailure,
  isConnectivityFailure,
  outcomeFailureProps,
  type AnalyticsFailureKind,
} from '@buxus/shared'
import { UnauthorizedHttpError } from '../abp/httpClient'
import { trackEvent } from '../analytics/webAnalytics'
import { isWebErrorReported, leaveWebBreadcrumb, notifyWebError } from './webErrorReporting'

export { FRIENDLY_CONNECTIVITY_MESSAGE }

/** Parse HTTP status embedded in `httpJson` / SAS fetch error messages. */
export function extractHttpStatusFromMessage(message: string): number | null {
  const patterns = [
    /SAS fetch failed (\d{3})/i,
    /Request failed \((\d{3})\)/i,
    /\b(\d{3})\s*:/,
  ]
  for (const pattern of patterns) {
    const match = message.match(pattern)
    if (match?.[1]) {
      const code = Number.parseInt(match[1], 10)
      if (code >= 100 && code <= 599) return code
    }
  }
  return null
}

export function redactSasUrl(sasUrl: string): { host: string; path: string } {
  try {
    const u = new URL(sasUrl)
    return { host: u.host, path: u.pathname }
  } catch {
    return { host: 'unknown', path: '' }
  }
}

/** @deprecated Prefer {@link isConnectivityFailure} from `@buxus/shared`. */
export function isBrowserNetworkFailure(error: unknown): boolean {
  return isConnectivityFailure(error)
}

/**
 * True for failures we should surface to Bugsnag / Mixpanel (including connectivity).
 * Excludes expected auth/not-found HTTP codes.
 */
export function isReportableServerOrNetworkFailure(error: unknown): boolean {
  if (error instanceof UnauthorizedHttpError) return false
  if (isConnectivityFailure(error)) return true
  if (error instanceof TypeError) return true
  if (error instanceof SyntaxError) return true
  if (error instanceof Error) {
    const status = extractHttpStatusFromMessage(error.message)
    if (status === 401 || status === 403 || status === 404) return false
    if (status !== null && status >= 500) return true
    if (status !== null && status >= 400 && status < 500) return false
    if (/success\s*=\s*false/i.test(error.message)) return true
    // Unknown Error — err on the side of reporting.
    return true
  }
  return false
}

function shouldReportSasFetchFailure(status: number | null, error: unknown): boolean {
  if (isConnectivityFailure(error)) return true
  if (error instanceof TypeError) return true
  if (status !== null && status >= 500) return true
  if (status === 401 || status === 403 || status === 404) return false
  if (status !== null && status >= 400) return false
  return isReportableServerOrNetworkFailure(error)
}

function failureKindForOperationalError(error: unknown): AnalyticsFailureKind {
  if (isConnectivityFailure(error)) return 'connectivity'
  if (error instanceof Error) {
    const status = extractHttpStatusFromMessage(error.message)
    if (status !== null && status >= 400) return 'http'
  }
  return classifyThrownFailure(error).failureKind
}

/**
 * Always report a reliability failure to Bugsnag, and to Mixpanel (batched / queued)
 * unless {@link skipAnalytics} is set.
 * Prefer enriching an existing outcome event via `analyticsEvent` so the event
 * type stays visible at the call site; otherwise emits
 * {@link AppEventTypes.NETWORK_REQUEST_FAILED}.
 */
export function reportReliabilityFailure(
  error: unknown,
  options: {
    context: string
    metadata?: Record<string, Record<string, unknown>>
    /** Existing Mixpanel event name (e.g. map_load_failed). Omit for catch-all. */
    analyticsEvent?: string
    analyticsProps?: Record<string, unknown>
    severity?: 'info' | 'warning' | 'error'
    failureKind?: AnalyticsFailureKind
    /** When true, Bugsnag only — no Mixpanel/GA. */
    skipAnalytics?: boolean
  },
): void {
  if (error instanceof UnauthorizedHttpError) return
  if (isWebErrorReported(error)) return

  const err =
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : String(error))
  const failureKind = options.failureKind ?? failureKindForOperationalError(error)
  const httpStatus = extractHttpStatusFromMessage(err.message) ?? undefined

  notifyWebError(err, {
    context: options.context,
    metadata: {
      ...options.metadata,
      reliability: {
        failureKind,
        ...(httpStatus != null ? { httpStatus } : {}),
      },
    },
    severity: options.severity,
  })

  if (options.skipAnalytics || options.analyticsProps?.ignored === true) {
    return
  }

  trackEvent(options.analyticsEvent ?? AppEventTypes.NETWORK_REQUEST_FAILED, {
    context: options.context,
    ...outcomeFailureProps({
      errorMessage: err.message,
      failureKind,
      httpStatus,
    }),
    ...options.analyticsProps,
  })
}

export function reportOperationalFailure(
  error: unknown,
  options: {
    context: string
    metadata?: Record<string, Record<string, unknown>>
  },
): void {
  if (!isReportableServerOrNetworkFailure(error)) return
  reportReliabilityFailure(error, options)
}

export function reportSasFetchFailure(
  error: unknown,
  meta: {
    kind: 'json' | 'blob'
    sasUrl: string
    httpStatus?: number
    /** Caller treated this as non-blocking (e.g. best-effort manifest file). */
    ignored?: boolean
  },
): void {
  const { host, path } = redactSasUrl(meta.sasUrl)
  const status =
    meta.httpStatus ??
    (error instanceof Error ? extractHttpStatusFromMessage(error.message) : null)

  leaveWebBreadcrumb(
    'sasFetch failed',
    {
      kind: meta.kind,
      host,
      path,
      status,
      message: error instanceof Error ? error.message : String(error),
      ...(meta.ignored ? { ignored: true } : {}),
    },
    'error',
  )

  if (!shouldReportSasFetchFailure(status, error)) return

  // Best-effort per-file fetch: breadcrumb only (download emits one summary).
  if (meta.ignored) {
    return
  }

  reportReliabilityFailure(error, {
    context: 'sasFetch',
    metadata: {
      sasFetch: {
        kind: meta.kind,
        host,
        path,
        status,
      },
    },
    analyticsEvent: AppEventTypes.NETWORK_REQUEST_FAILED,
    analyticsProps: {
      sasKind: meta.kind,
      host,
      path,
      ...(status != null ? { httpStatus: status } : {}),
    },
  })
}

/**
 * One Bugsnag + Mixpanel report when operator JSON downloaded successfully but
 * some best-effort manifest file cache steps failed (not one event per file).
 */
export function reportOperatorManifestFileCachePartial(meta: {
  operatorId: string
  name: string
  currentVersion: string
  manifestFileCount: number
  manifestFileFailureCount: number
  sampleErrorMessage?: string
  sampleDomExceptionName?: string
}): void {
  const {
    operatorId,
    name,
    currentVersion,
    manifestFileCount,
    manifestFileFailureCount,
    sampleErrorMessage,
    sampleDomExceptionName,
  } = meta

  leaveWebBreadcrumb(
    'operator manifest file cache partial',
    {
      operatorId,
      name,
      currentVersion,
      manifestFileCount,
      manifestFileFailureCount,
      ...(sampleErrorMessage ? { sampleErrorMessage } : {}),
      ...(sampleDomExceptionName ? { sampleDomExceptionName } : {}),
    },
    'error',
  )

  const err = new Error(
    `Operator manifest file cache incomplete: ${manifestFileFailureCount}/${manifestFileCount} failed` +
      (sampleErrorMessage ? ` (${sampleErrorMessage})` : ''),
  )

  notifyWebError(err, {
    context: 'operatorManifestFileCache',
    severity: 'warning',
    metadata: {
      download: {
        operatorId,
        name,
        currentVersion,
        manifestFileCount,
        manifestFileFailureCount,
        ...(sampleErrorMessage ? { sampleErrorMessage } : {}),
        ...(sampleDomExceptionName ? { sampleDomExceptionName } : {}),
      },
    },
  })

  trackEvent(AppEventTypes.OPERATOR_MANIFEST_FILE_CACHE_FAILED, {
    operatorId,
    name,
    currentVersion,
    manifestFileCount,
    manifestFileFailureCount,
    downloadSucceeded: true,
    ...(sampleErrorMessage ? { sampleErrorMessage } : {}),
    ...(sampleDomExceptionName ? { sampleDomExceptionName } : {}),
  })
}

/** Registration — Bugsnag only; Mixpanel uses registration_* outcome events at call sites. */
export function reportRegistrationFailureIfServer(error: unknown, step: string): void {
  if (!isReportableServerOrNetworkFailure(error)) return
  if (error instanceof UnauthorizedHttpError) return
  if (isWebErrorReported(error)) return

  const err =
    error instanceof Error ? error : new Error(typeof error === 'string' ? error : String(error))
  const failureKind = failureKindForOperationalError(error)

  notifyWebError(err, {
    context: 'registration',
    metadata: {
      registration: { step },
      reliability: { failureKind },
    },
  })
}
